import { functions, httpsCallable } from '@util/firebase';
import { PaymentDocument } from '@util/types/firestore/payment';
import * as Sentry from '@sentry/nextjs';
import Stripe from 'stripe';
import type { QueryClient } from '@tanstack/react-query';
import {
  CreatePaymentArgs,
  ExternalAccount,
  PayWithBalanceArgs,
  PayoutResponse,
  StripeAccountResponse,
  StripeTransaction,
  UpdatePaymentArgs,
} from './payments.types';

interface PaymentIntent {
  data: string;
  pi_id: string;
}

export class CartTotalMismatchError extends Error {
  readonly expected: number;
  readonly received: number;
  constructor(message: string, expected: number, received: number) {
    super(message);
    this.name = 'CartTotalMismatchError';
    this.expected = expected;
    this.received = received;
  }
}

export class ShippingUnavailableClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShippingUnavailableClientError';
  }
}

export class OutOfStockError extends Error {
  readonly productIds: string[];
  constructor(message: string, productIds: string[] = []) {
    super(message);
    this.name = 'OutOfStockError';
    this.productIds = productIds;
  }
}

export class ItemPriceChangedClientError extends Error {
  readonly productId: string;
  constructor(message: string, productId: string) {
    super(message);
    this.name = 'ItemPriceChangedClientError';
    this.productId = productId;
  }
}

function priceChangeError(
  err: { productId?: string; reason?: string },
  order: UpdatePaymentArgs['order'] | PayWithBalanceArgs['order']
): ItemPriceChangedClientError {
  const productId = typeof err.productId === 'string' ? err.productId : '';
  const title = Object.values(order.sellers ?? {}).flat()
    .find((item) => item.id === productId)?.product_title?.trim();
  const message = title
    ? `The price of ${title} changed (store sale ${err.reason === 'applied' ? 'started' : 'ended'}). Your cart has been updated.`
    : 'The price of an item in your cart changed. Please review your cart and try again.';
  return new ItemPriceChangedClientError(message, productId);
}

/** Refetch the saved cart, then apply fresh sale data to its local prices. */
export async function refreshCartAfterItemPriceChanged(queryClient: QueryClient, uid?: string) {
  // The cart query's onSuccess replaces local state. Do it before updating
  // sales so the repricing effect sees the latest cart instead of being undone.
  if (uid) await queryClient.refetchQueries(['cart', uid], { type: 'active' });
  // The normal active-sales response is edge-cached for 60 seconds. A unique
  // checkout URL bypasses that stale entry after the server rejects a price.
  try {
    const response = await fetch(`/api/store-sales/active?checkout_refresh=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Store sale refresh failed');
    queryClient.setQueryData(['store-sales-active'], await response.json());
  } catch {
    await queryClient.invalidateQueries(['store-sales-active']);
  }
}

// Vercel functions cap at maxDuration: 90; give the server an extra 5s to
// return its own timeout error before the client-side abort fires. If the
// connection itself is wedged (proxy/network), the abort surfaces a clean
// error instead of letting the fetch hang indefinitely.
const PAYMENT_FETCH_TIMEOUT_MS = 95_000;

export async function createPayment(
  args: CreatePaymentArgs,
  signal?: AbortSignal
): Promise<PaymentIntent> {
  const res = await fetch('/api/checkout/create-payment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `createPayment failed (${res.status})`);
  }

  return await res.json();
}

export async function updatePayment(
  args: UpdatePaymentArgs,
  signal?: AbortSignal
): Promise<PaymentIntent | null> {
  try {
    const res = await fetch('/api/checkout/update-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
      signal: signal ?? AbortSignal.timeout(PAYMENT_FETCH_TIMEOUT_MS),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // Surface cart-total-mismatch as a typed error so the checkout UI
      // shows the server's friendly message instead of the generic
      // "couldn't process your payment" fallback. Server already logged
      // the full breakdown to Sentry — don't double-log here.
      if (err?.error === 'cart_total_mismatch') {
        throw new CartTotalMismatchError(
          err.message ??
            'Your cart total has changed since checkout started. Please refresh your cart and try again.',
          err.expected,
          err.received
        );
      }
      // Surface shipping-unavailable so the checkout UI can show a specific
      // actionable message instead of the generic "couldn't process" fallback.
      if (err?.error === 'shipping_unavailable') {
        throw new ShippingUnavailableClientError(
          err.message ??
            "We couldn't calculate shipping to your address. This seller may not ship to your area — try a different address or contact support@gearfocus.com."
        );
      }
      // Out-of-stock is an expected buyer condition, not an API fault — surface
      // the actionable message and don't log to Sentry (the server already
      // logged at warning). Without this it falls through to the generic
      // "couldn't process your payment" and the buyer retries blindly (API-22).
      if (err?.error === 'out_of_stock') {
        throw new OutOfStockError(
          err.message ??
            'An item in your cart is no longer available. Please remove it from your cart to continue.',
          err.productIds ?? []
        );
      }
      if (err?.error === 'item_price_changed') {
        throw priceChangeError(err, args.order);
      }
      console.error('updatePayment API error', res.status, err);
      // Benign race (Sentry signature B1, GB #1498): mirrors the server-side
      // downgrade in update-payment.ts. The server returns the raw Stripe
      // rejection message as err.error for this case (response body is
      // otherwise unchanged), so the same two substrings + status match it
      // here. Without this, the client fires its own error-level capture for
      // the same benign event — WEB-YE hit 3x during the real 7/20 race, one
      // alert-rule threshold away from paging from the browser side even
      // with the server fix live. Still captured (not suppressed) so the
      // event stays visible for triage, just at warning.
      const isPostSuccessRace =
        res.status === 400 &&
        typeof err?.error === 'string' &&
        err.error.includes('payment_method_types') &&
        err.error.includes('status of succeeded');

      Sentry.captureMessage(
        isPostSuccessRace
          ? 'updatePayment API returned non-OK (benign post-success race)'
          : 'updatePayment API returned non-OK',
        {
          level: isPostSuccessRace ? 'warning' : 'error',
          tags: {
            fn: 'updatePayment',
            kind: isPostSuccessRace ? 'post_success_update_refused' : 'api_error',
          },
          extra: {
            status: res.status,
            statusText: res.statusText,
            body: err,
            pi_id: args.pi_id,
          },
        }
      );
      return null;
    }

    return await res.json();
  } catch (e) {
    // AbortError is expected on unmount/navigation — don't log to Sentry.
    if (e instanceof Error && e.name === 'AbortError') return null;
    // Re-throw typed errors so the caller can surface the specific message.
    if (e instanceof CartTotalMismatchError) throw e;
    if (e instanceof ShippingUnavailableClientError) throw e;
    if (e instanceof OutOfStockError) throw e;
    if (e instanceof ItemPriceChangedClientError) throw e;
    // TimeoutError comes from AbortSignal.timeout when the request exceeds
    // the client-side budget — likely a wedged connection or a server that
    // exceeded maxDuration. Log with a distinct tag so it's filterable from
    // generic network errors.
    if (e instanceof Error && e.name === 'TimeoutError') {
      console.error('updatePayment client timeout', e);
      Sentry.captureException(e, {
        tags: { fn: 'updatePayment', kind: 'client_timeout' },
        extra: { pi_id: args.pi_id, timeout_ms: PAYMENT_FETCH_TIMEOUT_MS },
      });
      return null;
    }
    console.error('updatePayment network error', e);
    Sentry.captureException(e, {
      tags: { fn: 'updatePayment', kind: 'network_error' },
      extra: { pi_id: args.pi_id },
    });
    return null;
  }
}

export async function payWithBalance(
  args: PayWithBalanceArgs,
  signal?: AbortSignal
): Promise<Stripe.Response<Stripe.Charge>> {
  const res = await fetch('/api/checkout/pay-with-balance', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
    signal: signal ?? AbortSignal.timeout(PAYMENT_FETCH_TIMEOUT_MS),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (err?.error === 'cart_total_mismatch') {
      throw new CartTotalMismatchError(
        err.message ??
          'Your cart total has changed since checkout started. Please refresh your cart and try again.',
        err.expected,
        err.received
      );
    }
    if (err?.error === 'shipping_unavailable') {
      throw new ShippingUnavailableClientError(
        err.message ??
          "We couldn't calculate shipping to your address. This seller may not ship to your area — try a different address or contact support@gearfocus.com."
      );
    }
    if (err?.error === 'out_of_stock') {
      throw new OutOfStockError(
        err.message ??
          'An item in your cart is no longer available. Please remove it from your cart to continue.',
        err.productIds ?? []
      );
    }
    if (err?.error === 'item_price_changed') {
      throw priceChangeError(err, args.order);
    }
    throw new Error(err.error || `payWithBalance failed (${res.status})`);
  }
  return await res.json();
}

export async function createSetupIntent(
  is_payout = false,
  signal?: AbortSignal
): Promise<PaymentIntent> {
  const res = await fetch('/api/checkout/create-setup-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_payout }),
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `createSetupIntent failed (${res.status})`);
  }
  return await res.json();
}

interface StripePaymentMethodResponse {
  data: Array<{
    id: string;
    card: {
      last4: string;
      brand: string;
      exp_month: number;
      exp_year: number;
    };
    billing_details: {
      name: string;
    };
  }>;
}

export async function getPaymentMethods(
  signal?: AbortSignal
): Promise<PaymentDocument[]> {
  const res = await fetch('/api/checkout/payment-methods', {
    method: 'GET',
    credentials: 'same-origin',
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `getPaymentMethods failed (${res.status})`);
  }
  const json = (await res.json()) as StripePaymentMethodResponse;

  return json.data.map((pm) => ({
    id: pm.id,
    last4: pm.card.last4,
    brand: pm.card.brand,
    name: pm.billing_details.name,
    expMonth: `${pm.card.exp_month}`,
    expYear: `${pm.card.exp_year}`,
    type: 0,
    saved: true,
  }));
}

export async function deleteCard(
  pm_id: string,
  signal?: AbortSignal
): Promise<Record<string, unknown>> {
  const res = await fetch('/api/checkout/delete-card', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payment_method: pm_id }),
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `deleteCard failed (${res.status})`);
  }
  return await res.json();
}

export async function getBalance() {
  const res = await httpsCallable<
    void,
    { available_balance: number; actual_pending: number; currency: string }
  >(
    functions,
    'getBalance'
  )();
  return res.data;
}

export async function getTransactionHistory() {
  const res = await httpsCallable<void, { data: StripeTransaction[] }>(
    functions,
    'listTransactions'
  )();
  return res.data?.data ?? [];
}

interface PayoutSellerArgs {
  amount: number;
  method: 'standard' | 'instant';
  destination: string;
  currency: string;
  requirements?: { [x: string]: { [x: string]: string } };
}

export async function payoutSeller(
  amount: number,
  method: 'standard' | 'instant',
  destination: string,
  currency = 'usd',
  requirements?: { [x: string]: { [x: string]: string } }
): Promise<PayoutResponse> {
  const res = await httpsCallable<PayoutSellerArgs, PayoutResponse>(
    functions,
    'payoutSeller'
  )({
    amount,
    method,
    destination,
    currency,
    requirements,
  });
  return res.data;
}

export async function getStripeAccount() {
  const res = await httpsCallable<void, StripeAccountResponse>(
    functions,
    'getSeller'
  )();
  return res.data;
}

interface UpdateSellerAccountArgs {
  requirements: Record<string, string>[];
}

export async function updateSellerAccount(
  requirements: Record<string, string>[]
) {
  const res = await httpsCallable<UpdateSellerAccountArgs, { status: boolean }>(
    functions,
    'updateStripeAccount'
  )({
    requirements,
  });
  return res.data;
}

interface PayoutMethodArgs {
  account_id: string;
  id: string;
}

export async function addSellerPayoutMethod(account_id: string, id: string) {
  const res = await httpsCallable<PayoutMethodArgs, ExternalAccount>(
    functions,
    'addSellerPayoutMethod'
  )({ account_id, id });
  return res.data;
}

export async function removeSellerPayoutMethod(account_id: string, id: string) {
  const res = await httpsCallable<PayoutMethodArgs, ExternalAccount>(
    functions,
    'removeExternalAccount'
  )({ account_id, id });
  return res.data;
}

interface ChargeAccountArgs {
  account_id: string;
  amount: number;
  description: string;
}

export async function chargeAccount(
  account_id: string,
  amount: number,
  description = ''
) {
  const res = await httpsCallable<ChargeAccountArgs, string>(
    functions,
    'chargeAccount'
  )({ account_id, amount, description });
  // returns the charge_id
  return res.data;
}

interface ChargeCreditCardArgs {
  paymentMethodId: string;
  customerId: string;
  amount: number;
  description: string;
}

export async function chargeCreditCard(
  paymentMethodId: string,
  customerId: string,
  amount: number,
  description = ''
) {
  const res = await httpsCallable<ChargeCreditCardArgs, string>(
    functions,
    'stripe-chargeCreditCard'
  )({ paymentMethodId, customerId, amount, description });
  // returns the charge_id
  return res.data;
}

interface CreateTransferArgs {
  account_id: string;
  amount: number;
  description: string;
}

export async function createTransfer(
  account_id: string,
  amount: number,
  description = ''
) {
  const res = await httpsCallable<CreateTransferArgs, string>(
    functions,
    'createTransfer'
  )({ account_id, amount, description });
  // returns the charge_id
  return res.data;
}

export async function getPaymentInfo(
  payment_id: string,
  signal?: AbortSignal
): Promise<Stripe.Charge | Stripe.PaymentIntent> {
  const res = await fetch('/api/checkout/payment-info', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payment_id }),
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `getPaymentInfo failed (${res.status})`);
    // Carry the HTTP status on the Error so callers can DISCRIMINATE without
    // parsing prose (GB #1709). The checkout return leg needs this: a 401/403
    // from `verifyApiAuth` is the expected outcome for a GUEST coming back
    // from Affirm — and guests are ~71% of Affirm buyers — so without the
    // status, a benign guest bounce is indistinguishable in Sentry from a 500
    // or a network drop. A status code is not PII.
    //
    // ADDITIVE ONLY: the message and the throw are unchanged, so the four
    // other callers (checkout-v3.tsx, order-confirmation/[id].tsx,
    // order-confirmation-v3/[id].tsx, and this page) keep their exact
    // behaviour. Nothing reads `.status` unless it opts in.
    (error as Error & { status?: number }).status = res.status;
    throw error;
  }
  return await res.json();
}
